import { Link, useNavigate, useRouterState } from '@tanstack/react-router';
import { Pin, Search, Star, X } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState, type ComponentType } from 'react';

import { focarAoAbrir } from '@/lib/foco';
import { notaDeModuloNaoConstruido } from '@/modulos/acesso/consultas';
import type { CaminhoConstruido, IdModulo } from '@/modulos/acesso/sprint';
import { useSessaoEmMemoria } from '@/modulos/sessao/memoria';

import { BotaoFechar } from '../Botao';
import { SimboloContrato } from '../Marca';
import { modulosDoMenu, secoesQueAbre } from '../secoes';
import { DETALHE_DA_TELA, RESUMO_DO_MODULO, ROTULO_CURTO } from './areas';
import {
  PREFERENCIAS_PADRAO,
  alternarFavorito,
  casaComABusca,
  chaveDasPreferencias,
  lerPreferencias,
  type PreferenciasDaNavegacao,
} from './preferencias';

/**
 * A NAVEGAÇÃO LATERAL do desenho que o dono entregou (`sidebar.zip`, 25/10).
 *
 * O que havia antes era uma sanfona dentro de uma coluna de 248 px: clicar num
 * módulo abria as telas dele empurrando os módulos de baixo, e a coluna
 * crescia e encolhia a cada clique. O desenho é outra coisa, e a diferença não
 * é estética:
 *
 *   TRILHO    só os módulos, sempre do mesmo tamanho. A lista de módulos nunca
 *             se move, então o lugar de cada um é sempre o mesmo lugar.
 *   PAINEL    abre AO LADO com as telas daquele módulo, agrupadas e com uma
 *             frase dizendo o que cada uma responde.
 *
 * ## As quatro coisas que o painel faz, e por que cada uma existe
 *
 * ALFINETE. Solto, o painel flutua por cima e fecha ao escolher uma tela — é o
 * caminho de quem vai a um lugar. Fixado, ele encosta no trilho e EMPURRA o
 * conteúdo: é o caminho de quem vai passar a manhã dentro do mesmo módulo e
 * não quer reabrir o menu a cada tela.
 *
 * BUSCA (`Ctrl K`). Com 51 telas, lembrar em que módulo mora cada uma é
 * trabalho que o sistema pode poupar. A busca atravessa os módulos e procura
 * também na descrição — que é onde está a palavra que a pessoa lembra
 * ("cupom", "balanço") quando não lembra o nome da tela.
 *
 * FAVORITOS. A estrela em cada linha. Quem usa duas telas o dia inteiro
 * deixa de atravessar o menu para chegar nelas.
 *
 * RECENTES NÃO EXISTEM, e isso é decisão do dono (26/10/2026: "não coloque
 * recentes"). O desenho os tinha; ele os recusou. Não ficaram escondidos — a
 * lista não é guardada em lugar nenhum, porque o computador do almoxarifado é
 * compartilhado e um histórico que ninguém pediu é um histórico que alguém lê.
 *
 * ## O que NÃO mudou, e é o que importa não quebrar
 *
 * O recorte por PERMISSÃO continua sendo o mesmo e vindo do mesmo lugar
 * (`modulosDoMenu`, `secoesQueAbre`): tela no menu é tela que o perfil abre, e
 * é isso que `menu-segue-a-tela.test.ts` cobra. A navegação ficou mais
 * elaborada; a regra de quem vê o quê não mudou uma linha.
 */

/* ------------------------------------------------------------------ tipos */

interface SecaoDoMenu {
  readonly rotulo: string;
  readonly para: CaminhoConstruido;
  readonly exige?: string;
}

interface ModuloDoMenu {
  readonly id: IdModulo;
  readonly rotulo: string;
  readonly para?: CaminhoConstruido;
  readonly Icone: ComponentType<{ className?: string }>;
  readonly secoes?: readonly SecaoDoMenu[];
  readonly ciclo?: string;
}

/** Uma tela já pronta para o painel: o que a lista tem mais o que a tabela diz. */
interface TelaDoPainel {
  readonly para: CaminhoConstruido;
  readonly rotulo: string;
  readonly grupo: string;
  readonly descricao: string;
  readonly Icone: ComponentType<{ className?: string }>;
  /** O módulo de onde ela veio — a busca e os favoritos mostram isso. */
  readonly modulo: string;
}

function telaDoPainel(secao: SecaoDoMenu, modulo: string): TelaDoPainel {
  const detalhe = DETALHE_DA_TELA[secao.para];
  return { para: secao.para, rotulo: secao.rotulo, modulo, ...detalhe };
}

/* ------------------------------------------------- as preferências, no aparelho */

function usePreferenciasDaNavegacao(usuarioId: string | null) {
  const chave = chaveDasPreferencias(usuarioId);
  const [preferencias, setPreferencias] = useState<PreferenciasDaNavegacao>(PREFERENCIAS_PADRAO);

  /* LER NO EFEITO, e não no `useState` inicial: o primeiro render tem de ser
     igual com e sem o que está guardado, senão a navegação pisca com os
     padrões e se reorganiza no quadro seguinte. */
  useEffect(() => {
    try {
      setPreferencias(lerPreferencias(window.localStorage.getItem(chave)));
    } catch {
      /* Janela anônima com dados de site bloqueados. Valem os padrões, e a
         navegação desenha igual — só não lembra nada na próxima vez. */
      setPreferencias(PREFERENCIAS_PADRAO);
    }
  }, [chave]);

  const mudar = useCallback(
    (como: (atual: PreferenciasDaNavegacao) => PreferenciasDaNavegacao) => {
      setPreferencias((atual) => {
        const novo = como(atual);
        if (novo === atual) return atual;
        try {
          window.localStorage.setItem(chave, JSON.stringify(novo));
        } catch {
          /* Não gravou: a escolha vale nesta sessão e não sobrevive ao
             recarregamento. É melhor que derrubar a navegação. */
        }
        return novo;
      });
    },
    [chave],
  );

  return [preferencias, mudar] as const;
}

/* ------------------------------------------------------------- o componente */

export function NavegacaoLateral({
  modulos: todos,
  moduloAtual,
  recortado,
  gavetaAberta = false,
  aoFecharGaveta,
}: {
  readonly modulos: readonly ModuloDoMenu[];
  readonly moduloAtual: IdModulo | undefined;
  /** A sessão já chegou? Muda só o texto do título, não o recorte. */
  readonly recortado: boolean;
  readonly gavetaAberta?: boolean;
  readonly aoFecharGaveta?: () => void;
}) {
  const caminho = useRouterState({ select: (estado) => estado.location.pathname });
  const navegar = useNavigate();
  const sessao = useSessaoEmMemoria();
  const permissoes = sessao?.permissoes ?? null;
  const alcanceParcial = sessao?.alcanceParcial === true;
  const [preferencias, mudarPreferencias] = usePreferenciasDaNavegacao(sessao?.usuarioId ?? null);

  const modulos = useMemo(
    () => modulosDoMenu(todos, permissoes, alcanceParcial),
    [todos, permissoes, alcanceParcial],
  );

  /** Toda tela que esta pessoa abre, de todos os módulos: a base da busca. */
  const todasAsTelas = useMemo<readonly TelaDoPainel[]>(
    () =>
      modulos.flatMap((modulo) =>
        secoesQueAbre(modulo.secoes ?? [], permissoes, alcanceParcial).map((s) => telaDoPainel(s, modulo.rotulo)),
      ),
    [modulos, permissoes, alcanceParcial],
  );

  const [abertoId, setAbertoId] = useState<IdModulo | null>(null);
  const [buscando, setBuscando] = useState(false);
  const [busca, setBusca] = useState('');
  const campoDeBusca = useRef<HTMLInputElement>(null);
  const painel = useRef<HTMLElement>(null);

  const aberto = buscando || abertoId !== null;
  const fixado = preferencias.fixado && aberto;

  const fechar = useCallback(() => {
    setAbertoId(null);
    setBuscando(false);
    setBusca('');
  }, []);

  /* Escolher uma tela fecha o painel SOLTO; o fixado continua aberto, que é o
     que fixar significa. */
  useEffect(() => {
    if (!preferencias.fixado) fechar();
  }, [caminho, preferencias.fixado, fechar]);

  /* `Ctrl K` abre a busca de qualquer lugar do sistema, e `Esc` fecha o painel
     solto. O atalho não rouba o teclado de quem está digitando num campo. */
  useEffect(() => {
    function aoTeclar(evento: KeyboardEvent) {
      if ((evento.ctrlKey || evento.metaKey) && evento.key.toLowerCase() === 'k') {
        evento.preventDefault();
        setBuscando(true);
        setAbertoId(null);
        window.setTimeout(() => campoDeBusca.current?.focus(), 0);
        return;
      }
      if (evento.key === 'Escape' && aberto && !preferencias.fixado) fechar();
    }
    document.addEventListener('keydown', aoTeclar);
    return () => {
      document.removeEventListener('keydown', aoTeclar);
    };
  }, [aberto, preferencias.fixado, fechar]);

  /* Clique fora fecha o painel SOLTO (p.29). O fixado é parte do layout: um
     clique na página não pode desmontá-lo. */
  useEffect(() => {
    if (!aberto || preferencias.fixado) return;
    function aoApontar(evento: PointerEvent) {
      if (!(evento.target instanceof Element)) return;
      if (evento.target.closest('[data-navegacao]')) return;
      fechar();
    }
    document.addEventListener('pointerdown', aoApontar);
    return () => {
      document.removeEventListener('pointerdown', aoApontar);
    };
  }, [aberto, preferencias.fixado, fechar]);

  /* O foco entra no painel solto e volta a quem o abriu, como toda
     sobreposição (p.29). O fixado não prende foco: ele não cobre nada. */
  useEffect(() => {
    if (!aberto || preferencias.fixado || buscando) return;
    const no = painel.current;
    const origem = document.activeElement;
    focarAoAbrir(no, no?.querySelector<HTMLElement>('a[href], button:not([disabled])') ?? undefined);
    return () => {
      const foco = document.activeElement;
      const aindaAqui = foco === null || foco === document.body || (no?.contains(foco) ?? false);
      if (aindaAqui && origem instanceof HTMLElement && origem.getClientRects().length > 0) origem.focus();
    };
  }, [aberto, preferencias.fixado, buscando]);

  const moduloAberto = modulos.find((m) => m.id === abertoId) ?? null;

  /** O que o painel lista agora: a busca, ou as telas do módulo aberto. */
  const telasDoPainel = useMemo<readonly TelaDoPainel[]>(() => {
    if (buscando) return todasAsTelas.filter((t) => casaComABusca(busca, [t.rotulo, t.modulo, t.descricao]));
    if (!moduloAberto) return [];
    return secoesQueAbre(moduloAberto.secoes ?? [], permissoes, alcanceParcial).map((s) =>
      telaDoPainel(s, moduloAberto.rotulo),
    );
  }, [buscando, busca, todasAsTelas, moduloAberto, permissoes, alcanceParcial]);

  /* As telas agrupadas, na ordem em que os grupos aparecem na lista. Na busca
     o grupo é o MÓDULO: "Controle" repetido em três módulos não diz onde a
     tela mora, e é exatamente isso que a busca precisa dizer. */
  const grupos = useMemo(() => {
    const porGrupo = new Map<string, TelaDoPainel[]>();
    for (const tela of telasDoPainel) {
      const chave = buscando ? tela.modulo : tela.grupo;
      porGrupo.set(chave, [...(porGrupo.get(chave) ?? []), tela]);
    }
    return [...porGrupo.entries()].map(([titulo, telas]) => ({ titulo, telas }));
  }, [telasDoPainel, buscando]);

  const favoritos = useMemo(
    () => preferencias.favoritos.map((id) => todasAsTelas.find((t) => t.para === id)).filter((t): t is TelaDoPainel => t !== undefined),
    [preferencias.favoritos, todasAsTelas],
  );

  /**
   * FIXADO NÃO FECHA — decisão do dono, 26/10/2026: *"e se tiver fixado não
   * deve poder permitir fechar"*.
   *
   * O ✕ já sumia, o `Esc` já não fechava e o clique fora também não. Faltava
   * o caminho que eu tinha deixado aberto: clicar no módulo JÁ ABERTO
   * recolhia o painel, e com ele a largura da tela mudava — o conteúdo
   * pulava de lugar por um clique que a pessoa deu para ir a outro módulo.
   *
   * Fixado, o painel só muda de assunto: clicar noutro módulo troca o que ele
   * mostra, e clicar no mesmo não faz nada. A única saída é o alfinete, que é
   * o que fixar significa.
   */
  const abrirModulo = useCallback(
    (id: IdModulo) => {
      setBuscando(false);
      setBusca('');
      setAbertoId((agora) => (agora === id && !preferencias.fixado ? null : id));
    },
    [preferencias.fixado],
  );

  const linhaDaTela = (tela: TelaDoPainel) => {
    const favorito = preferencias.favoritos.includes(tela.para);
    return (
      <li key={tela.para} className="group/linha relative">
        <Link
          to={tela.para}
          activeOptions={{ exact: true, includeSearch: false }}
          /* O TEXTO INTEIRO NO `title`: a descrição é cortada em duas linhas
             na tela, e quem quiser a frase toda a tem parando o mouse. */
          title={`${tela.rotulo} — ${tela.descricao}`}
          className="flex items-start gap-2 rounded-controle py-1.5 pr-8 pl-2 transition-colors hover:bg-rebaixada"
          activeProps={{ className: 'bg-selecao', 'aria-current': 'page' }}
          onClick={() => {
            if (!preferencias.fixado) fechar();
            aoFecharGaveta?.();
          }}
        >
          <tela.Icone aria-hidden="true" className="mt-1 size-[var(--icone-controle)] shrink-0 text-tinta-apagada" />
          <span className="flex min-w-0 flex-col">
            <span className="txt-corpo leading-snug text-pretty text-tinta">{tela.rotulo}</span>
            {/* DUAS LINHAS, E NÃO AS QUE O TEXTO QUISER. Sem o corte, uma
                descrição de quatro linhas num painel de 288 px fazia caber
                sete telas no lugar de doze — e a lista virava rolagem.
                Medido em 1366 px, que é a largura de tela mais comum. */}
            <span className="txt-linha2 line-clamp-2 leading-snug text-pretty text-tinta-secundaria">{tela.descricao}</span>
          </span>
        </Link>
        {/* A ESTRELA FICA SEMPRE NO DOM, e só aparece no hover, no foco ou
            quando já é favorito: quem navega por teclado precisa alcançá-la, e
            um botão que só existe com o mouse parado é um botão que metade das
            pessoas nunca encontra. */}
        <button
          type="button"
          aria-pressed={favorito}
          title={favorito ? `Tirar ${tela.rotulo} dos favoritos` : `Pôr ${tela.rotulo} nos favoritos`}
          aria-label={favorito ? `Tirar ${tela.rotulo} dos favoritos` : `Pôr ${tela.rotulo} nos favoritos`}
          onClick={() => {
            mudarPreferencias((p) => alternarFavorito(p, tela.para));
          }}
          className={`absolute top-1 right-0.5 inline-flex size-7 items-center justify-center rounded-controle transition-opacity hover:bg-rebaixada focus-visible:opacity-100 group-hover/linha:opacity-100 ${
            favorito ? 'text-marca opacity-100' : 'text-tinta-apagada opacity-0'
          }`}
        >
          <Star aria-hidden="true" className="size-[var(--icone-controle)]" {...(favorito ? { fill: 'currentColor' } : {})} />
        </button>
      </li>
    );
  };

  const painelAberto = aberto && (buscando || moduloAberto !== null);

  return (
    <>
      {/* O véu da gaveta (p.29), abaixo de 1024 px. */}
      {gavetaAberta ? (
        <div
          aria-hidden
          className="fixed inset-0 z-[var(--z-gaveta)] hidden bg-sobreposicao max-[1024px]:block"
          onClick={aoFecharGaveta}
        />
      ) : null}

      {/* ------------------------------------------------------------ trilho */}
      <nav
        data-navegacao
        aria-label="Módulos do sistema"
        data-gaveta={gavetaAberta ? 'aberta' : 'fechada'}
        style={{ width: preferencias.rotulos ? 'var(--menu-trilho-rotulos)' : 'var(--menu-trilho)' }}
        className="group/trilho my-3 ml-3 flex shrink-0 flex-col gap-3 overflow-hidden rounded-painel border border-borda-sutil bg-card py-3 shadow-superficie transition-[width] max-[1024px]:hidden data-[gaveta=aberta]:max-[1024px]:fixed data-[gaveta=aberta]:max-[1024px]:inset-y-0 data-[gaveta=aberta]:max-[1024px]:left-0 data-[gaveta=aberta]:max-[1024px]:z-[var(--z-gaveta)] data-[gaveta=aberta]:max-[1024px]:flex data-[gaveta=aberta]:max-[1024px]:w-[var(--menu-trilho-rotulos)]! data-[gaveta=aberta]:max-[1024px]:anima-gaveta"
      >
        {gavetaAberta && aoFecharGaveta ? (
          <div className="flex justify-center">
            <BotaoFechar rotulo="Fechar o menu" aoFechar={aoFecharGaveta} />
          </div>
        ) : null}

        {/* No trilho estreito vale o SIMBOLO isolado (p.04: mínimo 32 px), e
            não o logotipo inteiro: a 72 px ele sairia ilegível. A assinatura
            da Atenas continua na barra superior. */}
        <div className="flex justify-center px-1">
          <SimboloContrato />
        </div>

        {/* A BUSCA É O PRIMEIRO BOTÃO DO TRILHO, e não um campo perdido na
            barra de cima: ela é navegação, e o atalho que a abre (`Ctrl K`)
            está escrito nela para quem ainda não o conhece. */}
        <div className="px-2">
          <button
            type="button"
            title="Buscar tela · Ctrl K"
            aria-label="Buscar tela (Ctrl K)"
            aria-expanded={buscando}
            onClick={() => {
              setAbertoId(null);
              /* Fixado, a busca liga e não desliga pelo mesmo botão: desligar
                 fecharia o painel, e painel fixado não fecha. */
              setBuscando((agora) => (agora && !preferencias.fixado ? false : true));
              window.setTimeout(() => campoDeBusca.current?.focus(), 0);
            }}
            className={`flex w-full flex-col items-center gap-1 rounded-controle px-1 py-2 transition-colors hover:bg-rebaixada ${
              buscando ? 'bg-selecao text-tinta' : 'text-tinta-secundaria'
            }`}
          >
            <Search aria-hidden="true" className="size-[var(--icone-menu)] shrink-0" />
            {preferencias.rotulos ? <span className="txt-linha2 leading-tight">Buscar</span> : null}
          </button>
        </div>

        <ul className="flex min-h-0 flex-1 flex-col gap-1 overflow-x-hidden overflow-y-auto px-2">
          {modulos.map((modulo) => {
            const atual = moduloAtual === modulo.id;
            const temTelas = (modulo.secoes?.length ?? 0) > 0 && modulo.para !== undefined;
            const titulo = recortado ? `${modulo.rotulo} — ${RESUMO_DO_MODULO[modulo.id]}` : modulo.rotulo;
            if (!temTelas) {
              return (
                <li key={modulo.id}>
                  <span
                    aria-disabled="true"
                    title={`${modulo.rotulo}: ${notaDeModuloNaoConstruido(modulo)}`}
                    className="flex cursor-not-allowed flex-col items-center gap-1 rounded-controle px-1 py-2 text-tinta-apagada"
                  >
                    <modulo.Icone aria-hidden="true" className="size-[var(--icone-menu)] shrink-0" />
                    {preferencias.rotulos ? (
                      <span className="txt-linha2 w-full hyphens-auto text-center leading-tight text-pretty break-words">
                        {ROTULO_CURTO[modulo.id] ?? modulo.rotulo}
                      </span>
                    ) : null}
                  </span>
                </li>
              );
            }
            return (
              <li key={modulo.id}>
                <button
                  type="button"
                  title={titulo}
                  aria-expanded={abertoId === modulo.id}
                  aria-controls="nav-painel"
                  onClick={() => {
                    abrirModulo(modulo.id);
                  }}
                  onMouseEnter={() => {
                    if (preferencias.abrirAoPassar) {
                      setBuscando(false);
                      setAbertoId(modulo.id);
                    }
                  }}
                  className={`flex w-full flex-col items-center gap-1 rounded-controle px-1 py-2 transition-colors hover:bg-rebaixada ${
                    atual ? 'bg-selecao font-semibold text-tinta [&_svg]:text-marca' : 'text-tinta-secundaria'
                  }`}
                >
                  <modulo.Icone aria-hidden="true" className="size-[var(--icone-menu)] shrink-0" />
                  {preferencias.rotulos ? (
                    <span className="txt-linha2 w-full hyphens-auto text-center leading-tight text-pretty break-words">
                      {ROTULO_CURTO[modulo.id] ?? modulo.rotulo}
                    </span>
                  ) : null}
                </button>
              </li>
            );
          })}
        </ul>
      </nav>

      {/* ------------------------------------------------------------ painel */}
      {painelAberto ? (
        <aside
          ref={painel}
          data-navegacao
          id="nav-painel"
          aria-label={buscando ? 'Buscar tela' : `Telas de ${moduloAberto?.rotulo ?? ''}`}
          /* FIXADO entra no fluxo e empurra o conteúdo; SOLTO flutua por cima,
             ancorado à direita do trilho. São os dois estados do desenho, e a
             diferença de comportamento é a razão de o alfinete existir. */
          className={
            fixado
              ? 'my-3 ml-2 flex w-[var(--menu-painel)] shrink-0 flex-col overflow-hidden rounded-painel border border-borda-sutil bg-card shadow-superficie max-[1024px]:hidden'
              : 'anima-menu fixed top-3 bottom-3 left-[calc(var(--menu-trilho-rotulos)+1.25rem)] z-[var(--z-gaveta)] flex w-[var(--menu-painel)] flex-col overflow-hidden rounded-painel border border-borda-sutil bg-flutuante shadow-elevado max-[1024px]:hidden'
          }
        >
          <header className="flex flex-col gap-2 border-b border-borda-sutil px-3 py-2.5">
            <div className="flex items-start justify-between gap-2">
              <div className="flex min-w-0 flex-col">
                <h2 className="txt-secao truncate text-tinta">{buscando ? 'Buscar tela' : (moduloAberto?.rotulo ?? '')}</h2>
                <p className="txt-linha2 text-pretty text-tinta-secundaria">
                  {buscando
                    ? 'Em todos os módulos que você abre. Enter abre o primeiro.'
                    : moduloAberto
                      ? RESUMO_DO_MODULO[moduloAberto.id]
                      : ''}
                </p>
              </div>
              <div className="flex shrink-0 items-center gap-1">
                <button
                  type="button"
                  aria-pressed={preferencias.fixado}
                  title={preferencias.fixado ? 'Soltar painel' : 'Fixar painel'}
                  aria-label={preferencias.fixado ? 'Soltar painel' : 'Fixar painel'}
                  onClick={() => {
                    mudarPreferencias((p) => ({ ...p, fixado: !p.fixado }));
                  }}
                  className={`inline-flex size-8 items-center justify-center rounded-controle transition-colors hover:bg-rebaixada ${
                    preferencias.fixado ? 'bg-selecao text-marca' : 'text-tinta-secundaria'
                  }`}
                >
                  <Pin
                    aria-hidden="true"
                    className={`size-[var(--icone-controle)] transition-transform ${preferencias.fixado ? '' : 'rotate-45'}`}
                  />
                </button>
                {/* O ✕ só existe no painel SOLTO: o fixado não se fecha, se
                    solta — e dois jeitos de tirar o painel da frente fariam a
                    pessoa aprender dois. */}
                {preferencias.fixado ? null : (
                  <button
                    type="button"
                    title="Fechar painel"
                    aria-label="Fechar painel"
                    onClick={fechar}
                    className="inline-flex size-8 items-center justify-center rounded-controle text-tinta-secundaria transition-colors hover:bg-rebaixada"
                  >
                    <X aria-hidden="true" className="size-[var(--icone-controle)]" />
                  </button>
                )}
              </div>
            </div>

            {/* O FOCO FICA NO CONTÊINER, e não no `input`. A regra 8 do manual é
                que o anel NUNCA é removido; o `controle` do projeto troca o
                anel pela borda do foco com halo, e um campo composto —
                lupa + input + atalho — tem de mostrar isso na caixa inteira,
                senão o anel do input aparece dentro da borda, desenhando duas
                molduras para o mesmo foco. */}
            <div className="flex items-center gap-2 rounded-controle border border-borda bg-tela px-2 focus-within:border-[var(--foco)] focus-within:shadow-[0_0_0_3px_var(--marca-tinta-forte)]">
              <Search aria-hidden="true" className="size-[var(--icone-controle)] shrink-0 text-tinta-apagada" />
              <input
                ref={campoDeBusca}
                type="search"
                value={busca}
                placeholder="Buscar tela"
                aria-label="Buscar tela"
                className="txt-corpo min-w-0 flex-1 bg-transparent py-2 text-tinta outline-none"
                onChange={(evento) => {
                  setBusca(evento.target.value);
                  setBuscando(true);
                }}
                onFocus={() => {
                  setBuscando(true);
                }}
                onKeyDown={(evento) => {
                  if (evento.key !== 'Enter') return;
                  const primeira = telasDoPainel[0];
                  if (!primeira) return;
                  evento.preventDefault();
                  void navegar({ to: primeira.para });
                  if (!preferencias.fixado) fechar();
                }}
              />
              {busca === '' ? <span className="txt-selo shrink-0 font-[family-name:var(--fonte-codigo)] text-tinta-secundaria">Ctrl K</span> : null}
            </div>
          </header>

          <div className="flex min-h-0 flex-1 flex-col gap-3 overflow-y-auto px-2 py-2">
            {/* FAVORITOS E RECENTES só fora da busca: durante a busca a lista
                tem de ser só o que casa com o que foi digitado, senão a pessoa
                lê como resultado o que não é. */}
            {!buscando && favoritos.length > 0 ? (
              <Grupo titulo="Favoritos">{favoritos.map(linhaDaTela)}</Grupo>
            ) : null}

            {grupos.map(({ titulo, telas }) => (
              <Grupo key={titulo} titulo={titulo}>
                {telas.map(linhaDaTela)}
              </Grupo>
            ))}

            {buscando && telasDoPainel.length === 0 ? (
              <p className="txt-apoio px-2 text-pretty text-tinta-secundaria">
                Nenhuma tela com esse texto. Procure pelo que a tela faz — &quot;cobrança&quot;, &quot;balanço&quot;,
                &quot;vencimento&quot; — e não só pelo nome dela.
              </p>
            ) : null}
          </div>

          {/* As duas preferências do desenho, no pé do painel: elas são do
              painel, e é aqui que quem as quer está olhando. */}
          <div className="flex flex-col gap-1 border-t border-borda-sutil px-2 py-2">
            <Chave
              ligada={preferencias.abrirAoPassar}
              rotulo="Abrir ao passar o mouse"
              ajuda="O painel abre sem clicar no módulo."
              aoMudar={() => {
                mudarPreferencias((p) => ({ ...p, abrirAoPassar: !p.abrirAoPassar }));
              }}
            />
            <Chave
              ligada={preferencias.rotulos}
              rotulo="Rótulos no menu"
              ajuda="Mostra o nome embaixo de cada ícone."
              aoMudar={() => {
                mudarPreferencias((p) => ({ ...p, rotulos: !p.rotulos }));
              }}
            />
          </div>
        </aside>
      ) : null}
    </>
  );
}

function Grupo({ titulo, children }: { readonly titulo: string; readonly children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <span className="txt-sobretitulo px-2 text-tinta-apagada">{titulo}</span>
      <ul className="flex flex-col">{children}</ul>
    </div>
  );
}

function Chave({
  ligada,
  rotulo,
  ajuda,
  aoMudar,
}: {
  readonly ligada: boolean;
  readonly rotulo: string;
  readonly ajuda: string;
  readonly aoMudar: () => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={ligada}
      title={ajuda}
      onClick={aoMudar}
      className="flex items-center justify-between gap-3 rounded-controle px-2 py-2 text-left transition-colors hover:bg-rebaixada"
    >
      <span className="txt-apoio min-w-0 text-pretty text-tinta-secundaria">{rotulo}</span>
      <span
        aria-hidden="true"
        className={`inline-flex h-5 w-9 shrink-0 items-center rounded-pill p-0.5 transition-colors ${ligada ? 'bg-marca' : 'bg-rebaixada'}`}
      >
        <span className={`size-4 rounded-pill bg-card transition-transform ${ligada ? 'translate-x-4' : ''}`} />
      </span>
    </button>
  );
}
